// SEO utilities for localization
export const SUPPORTED_LOCALES = ['en', 'es', 'fr', 'de', 'pt'];

/**
 * Get OpenGraph locale from language code
 */
export const getOgLocale = (lang = 'en') => {
  const map = { 
    en: 'en_US', 
    es: 'es_ES', 
    fr: 'fr_FR',
    de: 'de_DE',
    pt: 'pt_PT'
  };
  return map[lang] || 'en_US';
};

/**
 * Get JSON-LD language format from language code
 */
export const getJsonLdLanguage = (lang = 'en') => {
  const map = { 
    en: 'en-US', 
    es: 'es-ES', 
    fr: 'fr-FR',
    de: 'de-DE',
    pt: 'pt-PT'
  };
  return map[lang] || 'en-US';
};

/**
 * Generate OpenGraph locale alternates
 */
export const generateOgLocaleAlternates = (currentLang = 'en') => {
  const alternates = SUPPORTED_LOCALES
    .filter(lang => lang !== currentLang)
    .map(lang => ({
      property: 'og:locale:alternate',
      content: getOgLocale(lang)
    }));
  
  return alternates;
};

/**
 * Get locale prefix for URLs
 */
export const getLocalePrefix = (lang = 'en') => {
  return lang === 'en' ? '' : `/${lang}`;
};

/**
 * Generate localized URL
 */
export const getLocalizedUrl = (baseUrl, path, lang = 'en') => {
  const prefix = getLocalePrefix(lang);
  const url = `${baseUrl}${prefix}${path}`;
  // Add trailing slash if path is empty (home page) and URL doesn't already end with one
  if (path === '' && !url.endsWith('/')) {
    return `${url}/`;
  }
  return url;
};

/**
 * Generate hreflang links for all supported locales
 */
export const generateHreflangLinks = (baseUrl, path, currentLang = 'en') => {
  const links = SUPPORTED_LOCALES.map(lang => ({
    rel: 'alternate',
    hreflang: lang,
    href: getLocalizedUrl(baseUrl, path, lang)
  }));
  
  // Add x-default (always points to root, not English subpath)
  // Only add if not already present to avoid duplicates
  const hasXDefault = links.some(link => link.hreflang === 'x-default');
  if (!hasXDefault) {
    // x-default should point to the root path, not the English subpath
    const rootUrl = `${baseUrl}${path}`;
    const normalizedRootUrl = path === '' && !rootUrl.endsWith('/') ? `${rootUrl}/` : rootUrl;
    links.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: normalizedRootUrl
    });
  }
  
  return links;
};

/**
 * Generate translation cross-references for articles
 */
export const generateTranslationLinks = (baseUrl, articleSlug, currentLang = 'en') => {
  const equivalents = SUPPORTED_LOCALES.map(lang => ({
    lang,
    url: getLocalizedUrl(baseUrl, `/guides/${articleSlug}`, lang)
  }));
  
  return {
    translationOfWork: currentLang !== 'en' 
      ? { "@id": equivalents.find(e => e.lang === 'en').url }
      : undefined,
    workTranslation: currentLang === 'en' 
      ? equivalents
          .filter(e => e.lang !== 'en')
          .map(e => ({ "@type": "Article", "@id": e.url }))
      : undefined
  };
};

/**
 * Clean markdown for schema (existing function moved here)
 */
export const cleanMarkdownForSchema = (text) => {
  if (!text) return '';
  return text
    .replace(/[*_#]/g, '') // Remove markdown formatting
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to plain text
    .replace(/\n+/g, ' ') // Replace newlines with spaces
    .trim();
};

/**
 * Normalize text to create clean ASCII slugs
 */
export const createCleanSlug = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD') // Decompose accented characters
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Replace glossary tokens with actual terms
 */
export const replaceGlossaryTokens = (text) => {
  if (!text) return '';
  return text
    // Handle GLOSARIO_XX tokens (with underscores)
    .replace(/GLOSARIO_2/g, '2025')
    .replace(/GLOSARIO_4/g, 'NIE')
    .replace(/GLOSARIO_5/g, 'TIE')
    .replace(/GLOSARIO_11/g, 'Visa Nómada Digital')
    .replace(/GLOSARIO_12/g, 'Ley de Nietos')
    .replace(/GLOSARIO_20/g, 'Zona de Bajas Emisiones (ZBE)')
    .replace(/GLOSARIO_29/g, 'Tarjeta Azul')
    .replace(/GLOSARIO_37/g, 'Madrid')
    .replace(/GLOSARIO_38/g, 'Barcelona')
    .replace(/GLOSARIO_39/g, 'Valencia')
    .replace(/GLOSARIO_21/g, 'IBAN español')
    .replace(/GLOSARIO_22/g, 'NIE')
    .replace(/GLOSARIO_23/g, 'TIE')
    // Handle GLOSSARY XX tokens (with spaces)
    .replace(/GLOSSARY 2/g, '2025')
    .replace(/GLOSSARY 4/g, 'NIE')
    .replace(/GLOSSARY 5/g, 'TIE')
    .replace(/GLOSSARY 11/g, 'Visa Nómada Digital')
    .replace(/GLOSSARY 12/g, 'Ley de Nietos')
    .replace(/GLOSSARY 29/g, 'Tarjeta Azul')
    .replace(/GLOSSARY 37/g, 'Beckham Law')
    .replace(/GLOSSARY 38/g, 'Régimen Especial de Tributación')
    .replace(/GLOSSARY 39/g, 'Régimen Fiscal Especial')
    .replace(/GLOSSARY 21/g, 'IBAN español')
    .replace(/GLOSSARY 22/g, 'NIE')
    .replace(/GLOSSARY 23/g, 'TIE')
    // Handle GLOSSARYXX tokens (no spaces)
    .replace(/GLOSSARY2/g, '2025')
    .replace(/GLOSSARY4/g, 'NIE')
    .replace(/GLOSSARY5/g, 'TIE')
    .replace(/GLOSSARY11/g, 'Visa Nómada Digital')
    .replace(/GLOSSARY12/g, 'Ley de Nietos')
    .replace(/GLOSSARY29/g, 'Tarjeta Azul')
    .replace(/GLOSSARY37/g, 'Beckham Law')
    .replace(/GLOSSARY38/g, 'Régimen Especial de Tributación')
    .replace(/GLOSSARY39/g, 'Régimen Fiscal Especial')
    .replace(/GLOSSARY21/g, 'IBAN español')
    .replace(/GLOSSARY22/g, 'NIE')
    .replace(/GLOSSARY23/g, 'TIE')
    // Handle GLOSARIOXX tokens (no underscores)
    .replace(/GLOSARIO2/g, '2025')
    .replace(/GLOSARIO4/g, 'NIE')
    .replace(/GLOSARIO5/g, 'TIE')
    .replace(/GLOSARIO11/g, 'Visa Nómada Digital')
    .replace(/GLOSARIO12/g, 'Ley de Nietos')
    .replace(/GLOSARIO29/g, 'Tarjeta Azul')
    .replace(/GLOSARIO37/g, 'Beckham Law')
    .replace(/GLOSARIO38/g, 'Régimen Especial de Tributación')
    .replace(/GLOSARIO39/g, 'Régimen Fiscal Especial')
    .replace(/GLOSARIO21/g, 'IBAN español')
    .replace(/GLOSARIO22/g, 'NIE')
    .replace(/GLOSARIO23/g, 'TIE')
    // Handle Spanish double-underscore tokens like __GLOSARIO_XX__
    .replace(/__GLOSARIO_(\d+)__/g, (m, n) => {
      const map = {
        1: '2024',
        2: '2025',
        4: 'NIE',
        5: 'TIE',
        11: 'Visa Nómada Digital',
        12: 'Ley de Nietos',
        20: 'Zona de Bajas Emisiones (ZBE)',
        18: 'Permiso de Regreso',
        19: 'ETIAS',
        21: 'IBAN español',
        22: 'NIE',
        23: 'TIE',
        29: 'Tarjeta Azul',
        37: 'Madrid',
        38: 'Barcelona',
        39: 'Valencia',
        41: 'Bilbao',
        42: 'Málaga',
        43: '€'
      };
      return map[Number(n)] || m;
    })
    // Handle French double-underscore tokens like __GLOSSAIRE_XX__
    .replace(/__GLOSSAIRE_(\d+)__/g, (m, n) => {
      const map = {
        0: 'NIE',
        1: 'TIE',
        2: '2025'
      };
      return map[Number(n)] || m;
    })
    // Handle GLOSARIO_XX without underscores
    .replace(/\bGLOSARIO_(\d+)\b/g, (m, n) => {
      const map = {
        1: '2024',
        2: '2025',
        4: 'NIE',
        5: 'TIE',
        11: 'Visa Nómada Digital',
        12: 'Ley de Nietos',
        20: 'Zona de Bajas Emisiones (ZBE)',
        21: 'IBAN español',
        22: 'NIE',
        23: 'TIE',
        29: 'Tarjeta Azul',
        37: 'Madrid',
        38: 'Barcelona',
        39: 'Valencia',
        41: 'Bilbao',
        42: 'Málaga',
        43: '€'
      };
      return map[Number(n)] || m;
    })
    // Handle template tokens like {{G:XX}}
    .replace(/\{\{G:2\}\}/g, '2025')
    .replace(/\{\{G:4\}\}/g, 'NIE')
    .replace(/\{\{G:5\}\}/g, 'TIE')
    .replace(/\{\{G:11\}\}/g, 'Visa Nómada Digital')
    .replace(/\{\{G:12\}\}/g, 'Ley de Nietos')
    .replace(/\{\{G:29\}\}/g, 'Tarjeta Azul')
    .replace(/\{\{G:37\}\}/g, 'Beckham Law')
    .replace(/\{\{G:38\}\}/g, 'Régimen Especial de Tributación')
    .replace(/\{\{G:39\}\}/g, 'Régimen Fiscal Especial')
    .replace(/\{\{G:21\}\}/g, 'IBAN español')
    .replace(/\{\{G:22\}\}/g, 'NIE')
    .replace(/\{\{G:23\}\}/g, 'TIE')
    // Handle GLOSSARY_XX__ tokens (with double underscores)
    .replace(/GLOSSARY_2__/g, '2025')
    .replace(/GLOSSARY_4__/g, 'NIE')
    .replace(/GLOSSARY_5__/g, 'TIE')
    .replace(/GLOSSARY_11__/g, 'Visa Nómada Digital')
    .replace(/GLOSSARY_12__/g, 'Ley de Nietos')
    .replace(/GLOSSARY_29__/g, 'Tarjeta Azul')
    .replace(/GLOSSARY_37__/g, 'Beckham Law')
    .replace(/GLOSSARY_38__/g, 'Régimen Especial de Tributación')
    .replace(/GLOSSARY_39__/g, 'Régimen Fiscal Especial')
    .replace(/GLOSSARY_21__/g, 'IBAN español')
    .replace(/GLOSSARY_22__/g, 'SEPA')
    .replace(/GLOSSARY_23__/g, 'TIE');
};

